
// pages/_app.tsx
import ScreenViewEvent from '@/components/DataLayer/ScreenViewEvent';
import Layout from '@/components/Layout';
import { LayoutProvider } from '@/contexts/layout';
import { ReservationProvider } from '@/contexts/reservation';
import reservation_state from '@/contexts/reservation_state';
import { SessionProvider } from '@/contexts/session';
import { SettingsProvider } from '@/contexts/settings';
import { clearReservationContext, getReservationContext } from '@/helpers/reservation-context';
import '@/styles/adyen.css';
import '@/styles/fonts.css';
import '@/styles/form.css';
import '@/styles/globals.css';
import { api } from "@/utils/api";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect } from 'react';
import 'react-loading-skeleton/dist/skeleton.css';
import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import { useSnapshot } from 'valtio';
import '../i18n';
import { CashbackProvider } from '@/contexts/cashback';

const queryClient = new QueryClient()

function MyApp({ Component, pageProps }: AppProps) {
  const routerInstance = useRouter();
  const snapshot = useSnapshot(reservation_state);

  useEffect(() => {
    const { query } = routerInstance;
    const reservationContext = getReservationContext();

    if (reservationContext) {
      clearReservationContext();

      reservation_state.itinerary = reservationContext.itinerary;
      reservation_state.itineraryReservation = reservationContext.itineraryReservation;
      reservation_state.rate = reservationContext.rate;
      reservation_state.rates = reservationContext.rates;
      reservation_state.optionals = reservationContext.optionals;
      reservation_state.renter = reservationContext.renter;
      reservation_state.estimate = reservationContext.estimate;
      reservation_state.depot = reservationContext.depot;
      reservation_state.returnDepot = reservationContext.returnDepot;
      reservation_state.vehicle = reservationContext.vehicle;
      reservation_state.vehicleReservation = reservationContext.vehicleReservation;
      reservation_state.returnDepotReservation = reservationContext.returnDepotReservation;
      reservation_state.vehicles = reservationContext.vehicles;
      reservation_state.isPrePaid = reservationContext.isPrePaid;
      reservation_state.paymentMethod = reservationContext.paymentMethod;
      reservation_state.reservation = reservationContext.reservation;
      reservation_state.user = reservationContext.user;
      reservation_state.cpdid = reservationContext.cpdid;
      reservation_state.pickup_store = reservationContext.pickup_store;
      reservation_state.pickup_date = reservationContext.pickup_date;
      reservation_state.pickup_time = reservationContext.pickup_time;
      reservation_state.return_store = reservationContext.return_store;
      reservation_state.return_date = reservationContext.return_date;
      reservation_state.return_time = reservationContext.return_time;
      reservation_state.driver = reservationContext.driver;
      reservation_state.coupon = reservationContext.coupon;
      reservation_state.allOptionals = reservationContext.allOptionals;
    } else {
      reservation_state.pickup_store = typeof query.pickup_store === 'string' ? query.pickup_store : undefined;
      reservation_state.pickup_date = typeof query.pickup_date === 'string' ? query.pickup_date : undefined;
      reservation_state.pickup_time = typeof query.pickup_time === 'string' ? query.pickup_time : undefined;
      reservation_state.return_store = typeof query.return_store === 'string' ? query.return_store : undefined;
      reservation_state.return_date = typeof query.return_date === 'string' ? query.return_date : undefined;
      reservation_state.return_time = typeof query.return_time === 'string' ? query.return_time : undefined;
      reservation_state.driver = typeof query.driver === 'string' ? query.driver : undefined;
      reservation_state.coupon = typeof query.coupon === 'string' ? query.coupon : undefined;
    }

  }, [routerInstance.query]);

  useEffect(() => {

    if (routerInstance.pathname === '/pagamento/verificacao') {
      return;
    }

    const targetQuery: any = {};
    if (snapshot.pickup_store) targetQuery.pickup_store = snapshot.pickup_store;
    if (snapshot.pickup_date) targetQuery.pickup_date = snapshot.pickup_date;
    if (snapshot.pickup_time) targetQuery.pickup_time = snapshot.pickup_time;
    if (snapshot.return_store) targetQuery.return_store = snapshot.return_store;
    if (snapshot.return_date) targetQuery.return_date = snapshot.return_date;
    if (snapshot.return_time) targetQuery.return_time = snapshot.return_time;
    if (snapshot.driver) targetQuery.driver = snapshot.driver;
    if (snapshot.coupon) targetQuery.coupon = snapshot.coupon;


    const currentQuery = routerInstance.query;

    const combinedQuery = { ...currentQuery, ...targetQuery };


    let queriesDiffer = false;
    const currentKeys = Object.keys(currentQuery).sort();
    const combinedKeys = Object.keys(combinedQuery).sort();

    if (currentKeys.length !== combinedKeys.length) {
      queriesDiffer = true;
    } else {
      for (const key of combinedKeys) {

        if (String(currentQuery[key]) !== String(combinedQuery[key])) {
          queriesDiffer = true;
          break;
        }
      }
    }


    if (queriesDiffer && routerInstance.isReady) {

      routerInstance.replace({
        pathname: routerInstance.pathname,
        query: combinedQuery,
      }, undefined, { shallow: true });
    }

  }, [
    snapshot.pickup_store,
    snapshot.pickup_date,
    snapshot.pickup_time,
    snapshot.return_store,
    snapshot.return_date,
    snapshot.return_time,
    snapshot.driver,
    snapshot.coupon,
  ]);

  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <SettingsProvider>
          <ReservationProvider>
            <CashbackProvider>
              <LayoutProvider>
                <ScreenViewEvent />
                <Layout page={routerInstance.route}>
                  <Component {...pageProps} />
                </Layout>
              </LayoutProvider>
            </CashbackProvider>
          </ReservationProvider>
        </SettingsProvider>
      </SessionProvider>
    </QueryClientProvider>
  );
}

export default api.withTRPC(MyApp);
